import React from "react"
import {
  PracticeProfileSubSectionIds,
  ProviderProfileSubSectionIds,
} from "../constants"
import { scrollToElement } from "../utils/scrollToElement"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { type BannerType } from "../../shared/components/Banner"
import cn from "../../../utils/classes"
import { Typography, typographyTypes } from "../../shared/components/Typography"
import Icon from "../../shared/components/Icon"
import useContentLayoutContext from "../../layout/useContentLayoutContext"

interface ProfileBannersProps {
  profileType: "providerProfile" | "practiceProfile"
  bannerData?: {
    showLicenseBanner: boolean
    licenseText?: string
    showLiabilityBanner: boolean
    liabilityText?: string
    liabilityClickable?: boolean
    liabilityType?: BannerType
    showDisclosureBanner: boolean
    disclosureText?: string
    showPracticeAgreementBanner?: boolean
    practiceAgreementText?: string
  }
  isPrivate: boolean
}

const ClickableStatusBanner: React.FC<{
  text: string
  clickable?: boolean
  onClick?: () => void
  type?: BannerType
  className?: string
}> = ({ text, onClick, type = "error", className = "", clickable = true }) => {
  // Map banner "type" to visual variants. We specifically support an "info" variant and a default "error" variant.
  const isInfo = type === ("info" as BannerType)

  const bgClass = isInfo ? "bg-blue-50" : "bg-red-50"
  const hoverClass = isInfo ? "hover:bg-blue-100" : "hover:bg-red-100"
  const textColorClass = isInfo ? "!text-blue-800" : "!text-aaa-error-red"
  const iconType = isInfo ? "infoQuestionmark" : "error"

  return (
    <div
      className={cn(
        `flex justify-between space-x-2 items-center w-full rounded-lg p-4 box-border ${bgClass}  ${clickable ? `cursor-pointer ${hoverClass} transition-colors` : ""} ${className}`,
      )}
      onClick={onClick}
    >
      <div className="flex items-center space-x-2">
        <Icon iconType={iconType} />
        <Typography
          text={text}
          variant={typographyTypes.BANNER_TITLE}
          styleOverrides={textColorClass}
        />
      </div>
    </div>
  )
}

const ProfileBanners: React.FC<ProfileBannersProps> = ({
  profileType,
  bannerData = {
    showLicenseBanner: false,
    showLiabilityBanner: false,
    showDisclosureBanner: false,
    showPracticeAgreementBanner: false,
  },
  isPrivate,
}) => {
  const { cms: cmsContent } = useCMSReady(["publicProfileSlide"])
  const { scrollableContainerRef } = useContentLayoutContext()
  const publicProfileContent = cmsContent.publicProfileSlide
  if (!bannerData) {
    return null
  }

  return (
    <>
      {bannerData.showLicenseBanner && (
        <ClickableStatusBanner
          text={bannerData.licenseText || ""}
          onClick={() =>
            scrollToElement(
              profileType === "providerProfile"
                ? ProviderProfileSubSectionIds.PROVIDER_PROFILE_QUALIFICATIONS_SECTION
                : PracticeProfileSubSectionIds.PRACTICE_PROFILE_THERAPIST_SECTION,
              scrollableContainerRef,
            )
          }
        />
      )}
      {bannerData.showLiabilityBanner && (
        <ClickableStatusBanner
          text={bannerData.liabilityText || ""}
          onClick={() =>
            bannerData.liabilityClickable &&
            scrollToElement(
              profileType === "providerProfile"
                ? ProviderProfileSubSectionIds.PROVIDER_PROFILE_LOCATIONS_SECTION
                : PracticeProfileSubSectionIds.PRACTICE_PROFILE_THERAPIST_SECTION,
              scrollableContainerRef,
            )
          }
          clickable={bannerData.liabilityClickable}
          type={bannerData.liabilityType}
        />
      )}
      {bannerData.showDisclosureBanner && (
        <ClickableStatusBanner
          text={bannerData.disclosureText || ""}
          onClick={() =>
            scrollToElement(
              PracticeProfileSubSectionIds.PRACTICE_PROFILE_THERAPIST_SECTION,
              scrollableContainerRef,
            )
          }
          clickable={profileType === "practiceProfile"}
        />
      )}
      {bannerData.showPracticeAgreementBanner && (
        <ClickableStatusBanner
          text={bannerData.practiceAgreementText || ""}
          clickable={false}
        />
      )}
      {isPrivate && (
        <ClickableStatusBanner
          text={publicProfileContent?.privateProfileBannerText ?? ""}
          type="info"
          clickable={false}
        />
      )}
    </>
  )
}

export default ProfileBanners
